import { ServiceItem } from '../types';
import { selectBillableServices } from './billingSelection';
import { contractedQuantity } from './nepPosition';
import { isHourlyUnit } from './serviceUnits';

/**
 * Laesst sich das Begehungs-Kontingent eines Projekts als ANZAHL lesen?
 *
 * Die Frage entscheidet, ob eine Oberflaeche „25 / 2444 Begehungen" zeigen darf.
 * Der Zaehler dort ist eine Stueckzahl (durchgefuehrte Berichte), der Nenner die
 * Summe der vereinbarten Mengen ueber die Begehungspositionen. Solange diese
 * Positionen in STUECK vereinbart sind, ist der Bruch eine echte Quote: 25 von
 * 40 vereinbarten Begehungen.
 *
 * Rechnen die Positionen dagegen nach ZEITAUFWAND ab, ist der Nenner ein
 * STUNDENBUDGET. Bei Projekt 6265-1 L standen dort 520 + 520 + 260 + 832 + 208 +
 * 104 = 2444 Std., und die Kachel las sich als „25 von 2444 Begehungen" — eine
 * Zahl, die es nie gab. Der Zaehler bleibt richtig, nur der Nenner ist keine
 * Begehungszahl; er wird in diesem Fall nicht mehr angezeigt.
 *
 * GEMISCHTER FALL — bewusst konservativ: sobald EINE Begehungsposition nach
 * Stunden abrechnet UND dafuer ein Kontingent vereinbart ist (quantityContracted
 * > 0), ist die Summe der vereinbarten Mengen keine Anzahl mehr, sondern
 * Stueck und Stunden addiert. Ein solcher Nenner ist eher irrefuehrend als der
 * fehlende, deshalb verschwindet er auch dann.
 *
 * Eine Stundenposition OHNE Kontingent (quantityContracted 0, z.B. eine
 * Position "nach Bedarf") traegt dagegen NICHTS zum Nenner bei — weder Stueck
 * noch Stunden — und darf ihn deshalb auch nicht kippen: bei
 * [{unit:'Stk', quantityContracted:40}, {unit:'Std.', quantityContracted:0}]
 * bleibt der Nenner 40, eine echte Stueckzahl, keine Mischung.
 *
 * OHNE Begehungsposition bleibt es beim Bestandsverhalten (true) — es gibt dann
 * ohnehin nichts anzuzeigen, und die Aufrufer sollen sich hier keine neue
 * Sonderregel einfangen.
 *
 * WELCHE Positionen diese Funktion als Begehung liest, entscheidet
 * selectBillableServices aus billingSelection: Zuordnung `inspection`,
 * hilfsweise der Titelarm fuer unklassifizierten Altbestand — NIE die
 * LV-Nummer, denn die Nummerierung gehoert dem Kunden und ist von Auftrag zu
 * Auftrag verschieden.
 *
 * ABWEICHUNG von der Kachel, die diese Funktion steuert: deren NENNER stammt
 * nicht von hier, sondern aus calculateStats
 * (src/components/project-hub/calculateStats.ts), und das erkennt Begehungen
 * ueber DREI Arme statt zwei — zusaetzlich per LV-Nummer (`2.7.*` / `2.8.*`),
 * auch ohne jede Zuordnung. Eine Alt-Position wie {lvPosition:'2.7.10',
 * title:'Betreuung Los 1', unit:'Std.', quantityContracted:520,
 * assignment: undefined} zaehlt calculateStats in den Nenner ein, taucht in
 * `inspections` unten aber gar nicht auf — diese Funktion sieht sie nicht und
 * kann sie folglich nicht als Stundenposition erkennen. Die Kachel zeigt in so
 * einem Fall weiterhin "25 / 520 Begehungen", genau das Problem, das diese
 * Funktion eigentlich beheben soll. Das ist eine bekannte Luecke, keine
 * Absicht: solange calculateStats seinen LV-Nummer-Arm behaelt, muesste diese
 * Funktion ihn spiegeln, um den Nenner lueckenlos abzudecken — separate
 * Aufgabe, nicht Teil dieser Aenderung.
 *
 * Stundenerkennung ausschliesslich ueber isHourlyUnit, damit „h", „Std." und
 * „Stunden" hier dieselbe Position beschreiben wie in den Buchungspfaden.
 */
export const isInspectionBudgetCountable = (services: ServiceItem[] | null | undefined): boolean => {
    // ignoreInspectionFlat: im Pauschalprojekt liefert die Buchungsauswahl nur
    // noch die Pauschale (Einheit Monat, nie stuendlich) — ohne die Option
    // stuende der Stundennenner „25 / 2444 Begehungen" dort wieder auf der
    // Kachel. Die Frage hier ist, welche Positionen Begehungen SIND.
    const inspections = selectBillableServices(services || [], 'inspection', { ignoreInspectionFlat: true });
    if (inspections.length === 0) return true;
    // NEP: Kontingent ist nur das Gebuchte (contractedQuantity) — eine
    // unbestellte Stunden-Eventualposition kippt den Nenner nicht.
    return !inspections.some(s => isHourlyUnit(s.unit) && contractedQuantity(s) > 0);
};
